/**
 * Scenario → Lucide icon resolution, shared by the scenario grid, the map
 * nodes, and the header dropdown so the icon is consistent everywhere
 * (Lucide components, never raw emoji). Extracted from ImpactPathView to
 * avoid a duplicate copy in ImpactPathHeader (importing the screen would be
 * a circular dependency).
 */
import {
  Monitor, Bot, Globe, Landmark, Users, GraduationCap, Coins, Leaf, Sprout,
  Heart, Zap, Lightbulb, Shield, Scale, Atom, Brain, Sun, Rocket, Network,
  Waypoints, Flame, Earth, BookOpen, Hexagon, Compass,
} from 'lucide-react';

// Keyword-to-Lucide icon mapping for scenario titles (fi + en)
const scenarioLucideKeywords = {
  'teknologia': Monitor, 'technology': Monitor, 'digi': Monitor, 'algoritm': Bot,
  'ilmasto': Globe, 'climate': Globe, 'sää': Globe,
  'demokratia': Landmark, 'democracy': Landmark, 'hallinto': Landmark, 'politiikka': Landmark, 'geopolit': Shield,
  'väestö': Users, 'population': Users, 'ikääntyminen': Users,
  'koulutus': GraduationCap, 'oppiminen': GraduationCap, 'education': GraduationCap, 'koulu': GraduationCap, 'talent': GraduationCap,
  'talous': Coins, 'economy': Coins, 'rahoitus': Coins,
  'ympäristö': Leaf, 'environment': Leaf, 'luonto': Leaf, 'kestävyys': Sprout, 'green transition': Sprout, 'green': Sprout,
  'terveys': Heart, 'health': Heart, 'hyvinvointi': Heart,
  'energia': Zap, 'energy': Zap,
  'innovaatio': Lightbulb, 'innovation': Lightbulb,
  'ruoka': Sprout, 'food': Sprout, 'maatalous': Sprout,
  'turvallisuus': Shield, 'security': Shield, 'jengi': Shield,
  'infrastr': Network, 'infrastructure': Network,
  'oikeus': Scale, 'laki': Scale, 'law': Scale,
  'tiede': Atom, 'science': Atom, 'tutkimus': Atom,
  'tekoäly': Brain, 'ai': Brain,
  'kukoistus': Sun, 'kasvu': Rocket,
  'irtikytkentä': Network, 'hajau': Network, 'hajoaminen': Network,
  'tasapaino': Scale, 'siirtymä': Waypoints,
};

// Emoji-to-Lucide mapping (for DB-stored emoji icons)
const emojiToLucide = {
  '🌿': Sprout, '🌱': Sprout, '🍃': Leaf,
  '🔥': Flame, '💥': Zap,
  '🤖': Bot, '💻': Monitor, '⚙️': Monitor, '💾': Network, '🖥️': Monitor,
  '🌍': Earth, '🌎': Earth, '🌏': Earth, '🌡️': Globe,
  '🏛️': Landmark, '🤝': Landmark,
  '👥': Users, '👤': Users,
  '📚': BookOpen, '🎓': GraduationCap,
  '💰': Coins, '📈': Coins,
  '❤️': Heart, '🏥': Heart,
  '⚡': Zap, '🔬': Atom,
  '💡': Lightbulb, '🚀': Rocket,
  '☀️': Sun, '🛡️': Shield,
};

// Varied fallback icons when nothing matches — cycles by index
const fallbackIcons = [Hexagon, Compass, Waypoints, Atom, Network, Lightbulb, Rocket, Brain];

export function getScenarioLucideIcon(title, icon, index = 0) {
  // 1. Try emoji from DB
  if (icon && emojiToLucide[icon]) return emojiToLucide[icon];

  // 2. Try keyword match on title
  if (title) {
    const normalized = title.toLowerCase().trim();
    for (const [key, Icon] of Object.entries(scenarioLucideKeywords)) {
      if (normalized.includes(key)) return Icon;
    }
  }

  // 3. Varied fallback by index
  return fallbackIcons[index % fallbackIcons.length];
}

export default getScenarioLucideIcon;
